"use client";

import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/03-presenters/components/ui/select";
import { cx } from "class-variance-authority";
import { Loader2, Search } from "lucide-react";
import { InspirationIntroduction } from "../Introduction";
import { useInspirationSearch, useInspirationSearchList, useSubmitInspirationSearch } from "./hooks";
import { toast } from "sonner";
import { useRouter } from "next/navigation";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/03-presenters/components/ui/command";
import { useEffect, useState } from "react";
import { LoadingOverlay } from "@mantine/core";
import { BrandIcon, CategoryIcon, ProductIcon } from "./icons";
import { countryStore } from "@/03-presenters/components/MultiCountrySelect";
import { countryList } from "@/00-shared/const";
import { InspirationKeywordExtension, InspirationKeywordType } from "./interface";
import { ImageSeriesField } from "@/01-core/entity/entities/entityV2";
import Ellipsis from "@/03-presenters/components/ui/Ellipsis";
import { ImageComp } from "@/03-presenters/components/ImageComp";
import Link from "next/link";
import { useQueryState } from "nuqs";
import { useMessages } from "next-intl";
function formatVideoCount(count: number) {
  if (count > 10000 && count < 100000000) {
    const wan = Math.floor(count / 10000);
    return `${wan}万+ `;
  }
  if (count > 100000000) {
    const yi = Math.floor(count / 100000000);
    return `${yi}亿+ `;
  }
  const base = Math.floor(count / 10) * 10;
  return `${base}+ `;
}
const timeFilterIcon = <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M1.4585 5.54169H12.5418V11.6667C12.5418 11.9889 12.2807 12.25 11.9585 12.25H2.04183C1.71966 12.25 1.4585 11.9889 1.4585 11.6667V5.54169Z" stroke="#1E286B" strokeWidth="1.16667" strokeLinejoin="round" />
    <path d="M1.4585 2.62502C1.4585 2.30285 1.71966 2.04169 2.04183 2.04169H11.9585C12.2807 2.04169 12.5418 2.30285 12.5418 2.62502V5.54169H1.4585V2.62502Z" stroke="#1E286B" strokeWidth="1.16667" strokeLinejoin="round" />
    <path d="M4.6665 1.16669V3.50002" stroke="#1E286B" strokeWidth="1.16667" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M9.3335 1.16669V3.50002" stroke="#1E286B" strokeWidth="1.16667" strokeLinecap="round" strokeLinejoin="round" />
  </svg>;
export default function InspirationSearch({
  firstRowKeywords,
  secondRowKeywords
}: {
  firstRowKeywords: string[];
  secondRowKeywords: string[];
}) {
  const {
    setKeyword
  } = useInspirationSearch();
  const router = useRouter();
  const {
    countryCode
  } = countryStore();
  const {
    runAsync
  } = useSubmitInspirationSearch();
  return <div className="flex w-[900px] flex-col items-center justify-center" data-sentry-component="InspirationSearch" data-sentry-source-file="InspirationSearch.tsx">
      <Link className="mt-4 text-sm text-primary underline" href="https://datanebula.feishu.cn/wiki/VS2mw0rEPiG62WkM5F6c1DQOnOf" target="_blank" data-sentry-element="Link" data-sentry-source-file="InspirationSearch.tsx">
        如何使用品牌素材检索，挖掘同行优质素材 ?
      </Link>
      <InspirationSearchInput data-sentry-element="InspirationSearchInput" data-sentry-source-file="InspirationSearch.tsx" />
      <MovingSearchList firstRowKeywords={firstRowKeywords} secondRowKeywords={secondRowKeywords} onSelect={async word => {
      setKeyword(word);
      const taskId = await runAsync({
        keyword: word
      });
      router.push(`/aiToolV2/inspiration/task/${taskId}?countryCode=${countryCode}`);
    }} data-sentry-element="MovingSearchList" data-sentry-source-file="InspirationSearch.tsx" />
      <InspirationIntroduction data-sentry-element="InspirationIntroduction" data-sentry-source-file="InspirationSearch.tsx" />
    </div>;
}
export function InspirationSearchInput() {
  const {
    data,
    loading: listSearchLoading,
    debouncedRun: debouncedRunListSearch
  } = useInspirationSearchList();
  const {
    keyword,
    setKeyword,
    dropDownLock
  } = useInspirationSearch();
  const [category] = useQueryState("category", {
    defaultValue: ""
  });
  const [productName] = useQueryState("productName", {
    defaultValue: ""
  });
  const i18nMap = useMessages();
  const {
    countryCode
  } = countryStore();
  const router = useRouter();
  const [isFocused, setIsFocused] = useState(false);
  const {
    runAsync,
    loading
  } = useSubmitInspirationSearch();
  const {
    products = [],
    brands = [],
    categories = []
  } = data ?? {};
  useEffect(() => {
    if (category) {
      setKeyword(i18nMap[category] as string);
      runAsync({
        type: InspirationKeywordType.CATEGORY,
        keyword: i18nMap[category] as string,
        category
      }).then(taskId => {
        router.push(`/aiToolV2/inspiration/task/${taskId}?countryCode=${countryCode}`);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category]);
  useEffect(() => {
    if (productName) {
      setKeyword(productName);
      runAsync({
        type: InspirationKeywordType.PRODUCT,
        keyword: productName
      }).then(taskId => {
        router.push(`/aiToolV2/inspiration/task/${taskId}?countryCode=${countryCode}`);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productName]);
  return <div className="mt-4 flex w-full flex-col gap-2 rounded-md bg-white py-2 shadow-[0_0_10px_rgba(0,0,0,0.1)]" data-sentry-component="InspirationSearchInput" data-sentry-source-file="InspirationSearch.tsx">
      <Command className="relative overflow-visible bg-transparent pl-2" shouldFilter={false} data-sentry-element="Command" data-sentry-source-file="InspirationSearch.tsx">
        <div className={cx("flex items-center justify-between rounded-md border-none bg-transparent pr-2", isFocused && "rounded-b-none")}>
          <div className="flex flex-1 items-center gap-1 px-1">
            <CommandInput value={keyword} hideSearchIcon disabled={loading} onFocus={() => setIsFocused(true)} onBlur={() => {
            setTimeout(() => {
              if (dropDownLock) return;
              setIsFocused(false);
            }, 100);
          }} inputWrapperClassName="border-none px-0 flex-1" className={cx("h-9 w-full border-none bg-transparent focus-visible:ring-transparent focus-visible:ring-offset-0")} placeholder="输入品牌、品类、产品关键词，检索最新爆款素材" style={{
            outline: "none"
          }} onValueChange={value => {
            setKeyword(value);
            debouncedRunListSearch(value);
          }} data-sentry-element="CommandInput" data-sentry-source-file="InspirationSearch.tsx" />
          </div>
          <button className="flex items-center gap-2 rounded-md bg-[#1E286B] px-3 py-[7px] text-sm font-medium text-white disabled:cursor-not-allowed disabled:opacity-50" disabled={loading} onClick={async () => {
          if (!keyword.trim()) {
            toast.warning("请输入关键词", {
              position: "top-center",
              richColors: true
            });
            return;
          }
          const taskId = await runAsync({
            keyword
          });
          router.push(`/aiToolV2/inspiration/task/${taskId}?countryCode=${countryCode}`);
        }}>
            <Search size={16} color="white" data-sentry-element="Search" data-sentry-source-file="InspirationSearch.tsx" />
            <span>搜索</span>
            {loading && <Loader2 className="size-4 animate-spin" />}
          </button>
        </div>
        <CommandList className={cx("absolute left-0 top-[calc(100%+1rem)] z-[20] max-h-[400px] w-full rounded-md bg-white p-4 shadow-[0_0_10px_rgba(0,0,0,0.2)] transition-opacity duration-200", isFocused && keyword.trim() ? "pointer-events-auto opacity-100" : "pointer-events-none opacity-0")} data-sentry-element="CommandList" data-sentry-source-file="InspirationSearch.tsx">
          <div className="flex items-center gap-2">
            <PublishDateFilter data-sentry-element="PublishDateFilter" data-sentry-source-file="InspirationSearch.tsx" />
            <HasProductRadio data-sentry-element="HasProductRadio" data-sentry-source-file="InspirationSearch.tsx" />
            <HasSponsorRadio data-sentry-element="HasSponsorRadio" data-sentry-source-file="InspirationSearch.tsx" />
          </div>
          <CommandGroup className="relative min-h-[60px]" data-sentry-element="CommandGroup" data-sentry-source-file="InspirationSearch.tsx">
            <LoadingOverlay visible={listSearchLoading} zIndex={11} overlayProps={{
            radius: "xs",
            blur: 2
          }} loaderProps={{
            size: 20
          }} data-sentry-element="LoadingOverlay" data-sentry-source-file="InspirationSearch.tsx" />
            <div className="mt-4 flex flex-col gap-2">
              <CommandItem className="group cursor-pointer rounded-md bg-[#F1F1FF] outline outline-1 outline-offset-0 outline-primary" onSelect={async () => {
              const taskId = await runAsync({
                keyword
              });
              router.push(`/aiToolV2/inspiration/task/${taskId}?countryCode=${countryCode}`);
            }} data-sentry-element="CommandItem" data-sentry-source-file="InspirationSearch.tsx">
                <div className="flex w-full items-center gap-1.5">
                  <Search size={14} color="#676A70" data-sentry-element="Search" data-sentry-source-file="InspirationSearch.tsx" />
                  <span className="font-medium text-[#1C1D1F] text-primary">
                    {`"${keyword}"`}
                  </span>
                </div>
              </CommandItem>
              {brands?.length ? <CommonSearchItem data={brands} icon={<BrandIcon />} label="品牌" onSelect={async (word, category) => {
              setKeyword(word);
              const taskId = await runAsync({
                type: InspirationKeywordType.BRAND,
                keyword: word,
                category
              });
              router.push(`/aiToolV2/inspiration/task/${taskId}?countryCode=${countryCode}`);
            }} /> : null}
              {categories?.length ? <CommonSearchItem data={categories} icon={<CategoryIcon />} label="类目" onSelect={async (word, category) => {
              setKeyword(word);
              const taskId = await runAsync({
                type: InspirationKeywordType.CATEGORY,
                keyword: word,
                category
              });
              router.push(`/aiToolV2/inspiration/task/${taskId}?countryCode=${countryCode}`);
            }} /> : null}
              {products?.length ? <CommonSearchItem data={products} icon={<ProductIcon />} label="商品" onSelect={async word => {
              setKeyword(word);
              const taskId = await runAsync({
                type: InspirationKeywordType.PRODUCT,
                keyword: word
              });
              router.push(`/aiToolV2/inspiration/task/${taskId}?countryCode=${countryCode}`);
            }} /> : null}
            </div>
          </CommandGroup>
        </CommandList>
      </Command>
    </div>;
}
export function PublishDateFilter() {
  const {
    lastXDays,
    setLastXDays,
    setDropDownLock
  } = useInspirationSearch();
  const items = [{
    label: "不限",
    value: "_ALL"
  }, {
    label: "最近 7 天",
    value: "7"
  }, {
    label: "最近 30 天",
    value: "30"
  }, {
    label: "最近 90 天",
    value: "90"
  }, {
    label: "最近 180 天",
    value: "180"
  }];
  const selectedItem = lastXDays ? items.find(item => item.value === lastXDays.toString()) : items[0];
  return <Select value={lastXDays ? lastXDays.toString() : "_ALL"} onValueChange={value => setLastXDays(value === "_ALL" ? undefined : parseInt(value))} onOpenChange={open => {
    setDropDownLock(open);
  }} data-sentry-element="Select" data-sentry-component="PublishDateFilter" data-sentry-source-file="InspirationSearch.tsx">
      <SelectTrigger className="flex h-9 w-fit min-w-[140px] items-center gap-1 font-medium text-[#676A70]" data-sentry-element="SelectTrigger" data-sentry-source-file="InspirationSearch.tsx">
        {timeFilterIcon}
        <SelectValue data-sentry-element="SelectValue" data-sentry-source-file="InspirationSearch.tsx">发布时间：{selectedItem?.label}</SelectValue>
      </SelectTrigger>
      <SelectContent data-sentry-element="SelectContent" data-sentry-source-file="InspirationSearch.tsx">
        {items.map(item => <SelectItem key={item.value} value={item.value}>
            {item.label}
          </SelectItem>)}
      </SelectContent>
    </Select>;
}
function CountryFilter() {
  const {
    setDropDownLock
  } = useInspirationSearch();
  const {
    countryCode,
    setCountryCode
  } = countryStore();
  const selectedItem = countryCode ? countryList.find(item => item.code === countryCode) : countryList[0];
  return <Select value={countryCode} onValueChange={setCountryCode} onOpenChange={open => {
    setDropDownLock(open);
  }} data-sentry-element="Select" data-sentry-component="CountryFilter" data-sentry-source-file="InspirationSearch.tsx">
      <SelectTrigger className="flex h-9 w-fit min-w-[140px] items-center gap-1 font-medium text-[#676A70]" data-sentry-element="SelectTrigger" data-sentry-source-file="InspirationSearch.tsx">
        <SelectValue data-sentry-element="SelectValue" data-sentry-source-file="InspirationSearch.tsx">国家：{selectedItem?.name}</SelectValue>
      </SelectTrigger>
      <SelectContent data-sentry-element="SelectContent" data-sentry-source-file="InspirationSearch.tsx">
        {countryList.map(item => <SelectItem key={item.code} value={item.code}>
            {item.name}
          </SelectItem>)}
      </SelectContent>
    </Select>;
}
export function HasProductRadio() {
  const {
    hasProduct,
    setHasProduct
  } = useInspirationSearch();
  return <CommonCheckBox isSelected={hasProduct} onCheckedChange={() => setHasProduct(!hasProduct)} label="仅搜索带货视频" data-sentry-element="CommonCheckBox" data-sentry-component="HasProductRadio" data-sentry-source-file="InspirationSearch.tsx" />;
}
export function HasSponsorRadio() {
  const {
    hasSponsor,
    setHasSponsor
  } = useInspirationSearch();
  return <CommonCheckBox isSelected={hasSponsor} onCheckedChange={() => setHasSponsor(!hasSponsor)} label="仅搜索投流视频" data-sentry-element="CommonCheckBox" data-sentry-component="HasSponsorRadio" data-sentry-source-file="InspirationSearch.tsx" />;
}
function CommonCheckBox({
  isSelected,
  onCheckedChange,
  label
}: {
  isSelected: boolean;
  onCheckedChange: () => void;
  label?: string;
}) {
  const {
    setDropDownLock
  } = useInspirationSearch();
  return <div className={cx("flex cursor-pointer items-center gap-2 rounded-sm border px-4 py-[7px]", isSelected ? "bg-[rgba(30,40,107,0.04)]" : "bg-white")} onFocus={() => setDropDownLock(true)} onBlur={() => setDropDownLock(false)} onClick={onCheckedChange} data-sentry-component="CommonCheckBox" data-sentry-source-file="InspirationSearch.tsx">
      <div className={cx("flex size-4 items-center justify-center rounded-full border", isSelected ? "border-[#1E286B]" : "border-[#ADB0B8]")}>
        {isSelected && <div className="size-2 rounded-full bg-[#1E286B]" />}
      </div>
      <span className={cx("text-sm font-medium", isSelected ? "text-[#1E286B]" : "text-[#676A70]")}>
        {label}
      </span>
    </div>;
}
function generateSearchItem(contentList: string[], onClick: (content: string) => void) {
  return contentList.map(content => <div key={content} className="mr-8 flex cursor-pointer items-center gap-2 rounded-full border px-3 py-1 text-sm text-[#676A70] hover:border-primary hover:text-primary" onClick={() => onClick(content)}>
      <span className="text-nowrap">{content}</span>
      <Search size={14} color="#676A70" />
    </div>);
}
function MovingSearchList({
  firstRowKeywords,
  secondRowKeywords,
  onSelect
}: {
  firstRowKeywords: string[];
  secondRowKeywords: string[];
  onSelect: (word: string) => void;
}) {
  return <div className="relative mt-4 flex w-full flex-col gap-3 overflow-hidden" data-sentry-component="MovingSearchList" data-sentry-source-file="InspirationSearch.tsx">
      <div className="absolute left-0 top-0 z-10 h-full w-12 bg-gradient-to-r from-white to-transparent" />
      <div className="absolute right-0 top-0 z-10 h-full w-12 bg-gradient-to-l from-white to-transparent" />
      <MovingSearchItem searchItem={generateSearchItem(firstRowKeywords, onSelect)} data-sentry-element="MovingSearchItem" data-sentry-source-file="InspirationSearch.tsx" />
      <MovingSearchItem direction="right" searchItem={generateSearchItem(secondRowKeywords, onSelect)} data-sentry-element="MovingSearchItem" data-sentry-source-file="InspirationSearch.tsx" />
    </div>;
}
function MovingSearchItem({
  searchItem,
  direction = "left"
}: {
  searchItem: React.ReactNode;
  direction?: "left" | "right";
}) {
  return <div className="inline-flex w-fit" style={{
    animation: `scroll-${direction} 20s linear infinite`
  }} onMouseEnter={e => {
    e.currentTarget.style.animationPlayState = "paused";
  }} onMouseLeave={e => {
    e.currentTarget.style.animationPlayState = "running";
  }} data-sentry-component="MovingSearchItem" data-sentry-source-file="InspirationSearch.tsx">
      <div className="inline-flex">{searchItem}</div>
      <div className="inline-flex">{searchItem}</div>
      <div className="inline-flex">{searchItem}</div>
      <div className="inline-flex">{searchItem}</div>
    </div>;
}
export function CommonSearchItem({
  data,
  icon,
  label,
  onSelect
}: {
  data: InspirationKeywordExtension["brands"] | InspirationKeywordExtension["products"] | InspirationKeywordExtension["categories"];
  icon: React.ReactNode;
  label: string;
  onSelect: (word: string, category?: string) => void;
}) {
  return <div className="flex flex-col gap-2" data-sentry-component="CommonSearchItem" data-sentry-source-file="InspirationSearch.tsx">
      <div className="flex items-center gap-1.5">
        {icon}
        <span className="text-sm text-[#676A70]">{label}</span>
      </div>
      {data.map(item => {
      const category = "l3_category" in item ? item.l3_category || item.l2_category || item.l1_category || "" : "";
      const imageSeries = "image_series" in item ? item.image_series : [] as ImageSeriesField["value"];
      const imageThumbUrl = imageSeries[0]?.thumb_url;
      const imageUrl = imageSeries[0]?.url;
      return <div key={item.word} onClick={() => onSelect(item.word, category)}>
            <CommandItem className="group cursor-pointer !bg-white outline-offset-0 hover:rounded-md hover:!bg-[#F1F1FF] hover:outline hover:outline-1 hover:outline-primary"
        // onSelect={() => onSelect(item.word, category)}
        >
              <div className="flex w-full items-center justify-between gap-8 pl-2">
                <div className="flex flex-1 items-center gap-2">
                  {imageThumbUrl && <ImageComp src={imageThumbUrl} className="size-10" originalImg={imageUrl || imageThumbUrl} fallbackImg="/fallbackImage/product.svg" />}
                  <div className="flex-1">
                    <Ellipsis title={item.word} ellipsis={2}>
                      <span className="text-[#1C1D1F] group-hover:font-medium group-hover:text-primary">
                        {item.word}
                      </span>
                    </Ellipsis>
                  </div>
                </div>
                <div className="text-nowrap rounded-md bg-[#F2F2FF] px-2 py-1 text-center text-[#676A70] group-hover:bg-white group-hover:text-primary">
                  {formatVideoCount(item.video_count) + "视频"}
                </div>
              </div>
            </CommandItem>
          </div>;
    })}
    </div>;
}